/*
 * In-App-Confirm-Dialog: ersetzt browser-eigene `window.confirm`-Boxen die
 * (a) hässlich sind, (b) den ganzen Chrome-Look sprengen, (c) nicht
 * stylable oder mit erweiterten Layouts (Warn-Hinweis, gefährliche Aktion)
 * anpassbar sind.
 *
 * Nutzung:
 *   const ok = await showConfirm({
 *     title: 'Session verwerfen?',
 *     message: 'Sie wird als Discarded markiert…',
 *     confirmLabel: 'Verwerfen',
 *     danger: true,
 *   });
 *   if (!ok) return;
 *
 * Nur ein Dialog gleichzeitig — wenn zwei Aufrufer parallel showConfirm()
 * feuern gewinnt der zweite (der erste wird mit false resolved). Reicht für
 * den Bedien-Fluss; ein Queue wäre für die Nutzer eher verwirrend.
 */

import { signal } from '@preact/signals';
import { useEffect, useRef } from 'preact/hooks';
import './Confirmer.css';

interface ConfirmSpec {
  id: string;
  title: string;
  message?: string;
  confirmLabel: string;
  cancelLabel: string;
  danger: boolean;
  resolve(result: boolean): void;
}

const active = signal<ConfirmSpec | null>(null);

export interface ShowConfirmInput {
  title: string;
  message?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Rotes Aussehen für Delete- / Verwerfen-Aktionen. Default false. */
  danger?: boolean;
}

export function showConfirm(input: ShowConfirmInput): Promise<boolean> {
  return new Promise((resolve) => {
    // Alten Dialog abbrechen wenn parallel — der neue gewinnt.
    if (active.value) active.value.resolve(false);
    active.value = {
      id: crypto.randomUUID(),
      title: input.title,
      message: input.message,
      confirmLabel: input.confirmLabel ?? 'OK',
      cancelLabel: input.cancelLabel ?? 'Abbrechen',
      danger: input.danger ?? false,
      resolve,
    };
  });
}

function resolveAndClear(result: boolean): void {
  const cur = active.value;
  if (!cur) return;
  active.value = null;
  cur.resolve(result);
}

export function Confirmer() {
  const spec = active.value;
  const confirmBtnRef = useRef<HTMLButtonElement | null>(null);
  useEffect(() => {
    if (!spec) return;
    // Autofokus auf Confirm — Enter reicht dann als Bestätigung.
    confirmBtnRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') resolveAndClear(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [spec?.id]);

  if (!spec) return null;
  return (
    <div class="hv-confirm-backdrop" onClick={() => resolveAndClear(false)}>
      <div class={'hv-confirm' + (spec.danger ? ' hv-confirm--danger' : '')}
           role="alertdialog"
           aria-labelledby={`${spec.id}-t`}
           aria-describedby={spec.message ? `${spec.id}-m` : undefined}
           onClick={(e) => e.stopPropagation()}>
        <h2 id={`${spec.id}-t`} class="hv-confirm__title">{spec.title}</h2>
        {spec.message && (
          <p id={`${spec.id}-m`} class="hv-confirm__message">{spec.message}</p>
        )}
        <div class="hv-confirm__actions">
          <button type="button"
                  class="hv-btn"
                  onClick={() => resolveAndClear(false)}>
            {spec.cancelLabel}
          </button>
          <button type="button"
                  ref={confirmBtnRef}
                  class={'hv-btn ' + (spec.danger ? 'hv-btn--danger' : 'hv-btn--primary')}
                  onClick={() => resolveAndClear(true)}>
            {spec.confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
